import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import media from '@/styles/media';
import theme from '@/styles/theme';
import { actions } from '@/store';
import postCheckEmailStatus from '@/fetches/user/auth/post-check-email-status';
import { EAuthModalPage } from '@/store/types';
import validator from '@/constant/valitator';
import postLoginBySocial from '@/fetches/user/auth/post-login-with-social';
import Message from '@/constant/message';
import postLoginByEmail from '@/fetches/user/auth/post-login-by-email';
import useAlert from '@/hooks/use-alert';
import { IS_PROD } from '@/constant/config';
import LoadingSpinner from '@/components/pages/user/common/loading-spinner';
import useSelectorUser from '@/hooks/use-selector-user';
import { ButtonLogo, RoundButton } from '../common/auth-button';
import { BasicButton, BasicButtonLabel, ButtonDiv } from '../common/new-auth-button';
import { Input } from '../common/new-auth-input';
import { TextLink, Title } from '../common/new-auth-text';
import {
  Container, ContentDiv, RightContainer, Wrapper,
} from '../common/new-auth-container';
import MobileLogo from '../common/auth-layout/mobile-logo';

/** 로그인 컴포넌트 */
export const InputEmail = () => {
  const { gotoAuthRegister, gotoAuthReset } = useSelectorUser('authModal');
  const [email, setEmail] = useState<string>('');
  const [pw, setPw] = useState<string>('');
  const [isEmail, setIsEmail] = useState<boolean>();
  const [isPw, setIsPw] = useState<boolean>();
  const [emailModal, setEmailModal] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { alert } = useAlert();
  const dispatch = useDispatch();

  const onChangeIdInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
    setIsEmail(!!value);
  }, []);

  const onFocusOutEmail = useCallback(() => {
    setEmailModal('');
  }, []);

  const onFocusInEmail = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const valid = t(validator.checkEmail(value));
    setEmailModal(valid);
  }, [t]);

  const onChangePwInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPw(value);

    if (value.length > 0) {
      setIsPw(true);
    } else if (value.length === 0) {
      setIsPw(undefined);
    } else {
      setIsPw(false);
    }
  }, []);

  const gotoRegister = useCallback(() => {
    if (gotoAuthRegister) { gotoAuthRegister(); }
  }, [gotoAuthRegister]);

  const gotoFindPw = useCallback(() => {
    if (gotoAuthReset) { gotoAuthReset(); }
  }, [gotoAuthReset]);

  const onLoginByEmail = useCallback(async () => {
    const [id, domain = 'steamup.academy'] = email.split('@');

    const { data, error, deletedAt } = await postLoginByEmail(`${id}@${domain}`, pw);
    if (data) {
      dispatch(actions.user.authModal.setToken(data));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SUCCESS));
      return;
    }

    switch (error) {
      case 'incorrect password':
        alert(t(Message.아이디또는비밀번호를잘못입력했습니다));
        break;
      case 'member is not enabled':
        // TODO 문구변경
        alert(t(Message.서비스이용에불편을드려죄송합니다));
        break;
      case 'too many failure':
        await alert(
          t(Message.비밀번호를5회이상잘못입력하셨습니다비밀번호찾기를통해비밀번호를변경해주세요),
        );
        dispatch(actions.user.authModal.setConfirmCodeType('pwreset'));
        dispatch(actions.user.authModal.setEmail(email));
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.AUTH_EMAIL));
        if (gotoAuthReset) { gotoAuthReset(); }// Check: 비밀번호 재설정 페이지로 페이지 이동
        break;
      case 'member is pending deletion':
        dispatch(actions.user.authModal.setDeletedAt(deletedAt!));
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.WILL_BE_DELETED_ACCOUNT));
        break;
      default:
        await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
        break;
    }
  }, [alert, dispatch, email, gotoAuthReset, pw, t]);

  /** 이메일이 있는지 확인 -> 이메일 로그인 */
  const onClickLogin = useCallback(async () => {
    dispatch(actions.user.authModal.setEmail(email));
    dispatch(actions.user.authModal.setConfirmCodeType('email'));

    setLoading(true);
    const [id, domain = 'steamup.academy'] = email.split('@');
    const result = await postCheckEmailStatus(`${id}@${domain}`);
    switch (result) {
      case 'email exists with password':
        onLoginByEmail();
        break;
      case 'email exists without password':
        dispatch(actions.user.authModal.setEmail(email));
        if (gotoAuthReset) { gotoAuthReset(); }
        break;
      case 'email does not exist':
        await alert(t(Message.아이디또는비밀번호를잘못입력했습니다));
        break;
      case 'not email format':
        await alert(t(Message.아이디또는비밀번호를잘못입력했습니다));
        break;
      case 'error':
        await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
        break;
      default:
    }
    setLoading(false);
  }, [dispatch, email, onLoginByEmail, gotoAuthReset, alert, t]);

  /** 소셜 로그인 */
  const onClickSocialLogin = useCallback((socialType: Parameters<typeof postLoginBySocial>[0]) => async () => {
    dispatch(actions.user.authModal.setConfirmCodeType('email'));
    const { data, error } = await postLoginBySocial(socialType);
    if (error) {
      if (error === 'user close') await alert(t(Message.로그인이취소되었습니다));
      if (error.split(':')[0] === 'will be deleted at') {
        const deletedAt = new Date(Number(error.split(':')[1]));
        dispatch(actions.user.authModal.setDeletedAt(deletedAt));
        dispatch(actions.user.authModal.setEmail(data!.email));
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.WILL_BE_DELETED_ACCOUNT));
      }
    } else if (!data) {
      await alert(t(Message.로그인정보가없습니다));
    } else if (!data.email) {
      dispatch(actions.user.authModal.setConfirmCodeType('social'));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.AUTH_EMAIL));
    } else {
      dispatch(actions.user.authModal.setToken({ at: data.at, rt: data.rt }));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SUCCESS));
    }
  }, [dispatch, alert, t]);

  const handleOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    const disabled = !(isEmail && isPw) && !loading;
    if (e.key === 'Enter') {
      if (!disabled) {
        onClickLogin();
      }
    }
  }, [isEmail, isPw, loading, onClickLogin]);

  return (
    <Wrapper>
      <ButtonDiv>
        <BasicButtonLabel>
          {`${t(Message.계정이없으신가요)}`}
        </BasicButtonLabel>
        <MobileLogo />
        <BasicButton className="signUP" onClick={gotoRegister}>
          {t(Message.회원가입)}
        </BasicButton>
      </ButtonDiv>

      <ContentDiv>
        <Title>{t(Message.로그인)}</Title>
        <Input
          onChange={onChangeIdInput}
          onKeyPress={handleOnKeyPress}
          placeholder={t(Message.이메일을입력하세요)}
          validate={isEmail}
          value={email}
          onBlur={onFocusOutEmail}
          onFocus={onFocusInEmail}
          autoComplete="email"
        />
        {/* {(emailModal !== '' && isEmail !== undefined) && <BalloonModal fade={isEmail} text={emailModal} />} */}
        <Input
          onChange={onChangePwInput}
          onKeyPress={handleOnKeyPress}
          placeholder={t(Message.비밀번호를입력하세요)}
          validate={isPw}
          type="password"
          autoComplete="password"
        />
        <RightContainer>
          <TextLink onClick={gotoFindPw}>
            {t(Message.비밀번호를잊으셨나요)}
          </TextLink>
        </RightContainer>
        <Button
          type="button"
          onClick={onClickLogin}
          disabled={!(isEmail && isPw) && !loading}
          className="login"
        >
          {loading ? (<LoadingSpinner />) : t(Message.로그인)}
        </Button>
        <MobileSocialLoginTitle>
          <Line />
          <SimpleLoginTitle>{t(Message.간편로그인)}</SimpleLoginTitle>
          <Line />
        </MobileSocialLoginTitle>
        <Container className="social-login">
          <RoundButton onClick={onClickSocialLogin('google')}>
            <ButtonLogo src="/assets/images/auth/google-logo.png" alt="google" />
            <div>{t(Message.Google로계속하기)}</div>
          </RoundButton>
          <RoundButton onClick={onClickSocialLogin('facebook')}>
            <ButtonLogo src="/assets/images/auth/facebook-logo.png" alt="facebook" />
            <div>{t(Message.Facebook으로계속하기)}</div>
          </RoundButton>
        </Container>
        {!IS_PROD && (
          <RoundButton
            type="button"
            className="test"
            onClick={onClickSocialLogin('test')}
          >
            {t(Message.테스트계정로그인)}
          </RoundButton>
        )}
      </ContentDiv>

    </Wrapper>
  );
};

const Button = styled(BasicButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

export const MobileLogoWrap = styled.div`
  width: 145px;
  height: 32px;
  display: none;
  > img {
    width: 100%;
  }
  ${media.lg`
    display: block;
  `}
  ${media.xs`
    width: 82px;
    height: 18px;
  `}

  &.reset-password {
    padding: 40px 0 120px 0;
  }
`;

export const MobileSocialLoginTitle = styled.div`
  display: none;
  ${media.lg`
    display: flex;
    align-items: center;
    gap: 20px;
    padding-top: 50px; // 230px
    /* position: fixed;
    bottom: 17%; */
    width: 90%;
    max-width: 420px;
  `}
`;

export const Line = styled.div`
  flex: 1;
  height: 0px;
  border: 1px solid #d2e0f3;
`;

export const SimpleLoginTitle = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: ${theme.fontSize.small};
  color: #5a6089;
`;
